import DateRangeIcon from '@mui/icons-material/DateRange';
import { FormControl, InputLabel, TextField, Typography } from '@mui/material';
import { ChangeEvent, Dispatch, FC, memo, useCallback } from 'react';
import {
  ArticleFormat,
  ArticleStatus,
  BrandKey,
  CategoryFormat,
  CategoryStatus,
  GetBrandCategoriesQuery,
  GetBrandCategoriesQueryVariables,
} from '../../../__generated__/queries-web';
import { client } from '../../../apollo';
import { Suggest } from '../../../components/Suggest';
import { getTitleFromBrandKey } from '../../../utils/brands';
import { formatDate } from '../../../utils/dateUtils';
import { GET_BRAND_CATEGORIES } from '../../SlideshowEdit/CategorySelector/getBrandCategories.web.graphql';
import { SourceInput } from '../ArticleEditor/SourceInput';
import { ArticleEditAction, ArticleState, actions } from '../reducer';
import { LinksContent } from './LinksContent';
import { LinksToTopic } from './LinksToTopic';
import { Qualifiers } from './Qualifiers';
import { useStyles } from './styles';

type DrawerContentProps = {
  subjectId?: string;
  subjectTitle?: string;
  renderInput: (inputName: string) => boolean;
  dispatch: Dispatch<ArticleEditAction>;
} & Pick<
  ArticleState,
  | 'publishedAt'
  | 'categories'
  | 'tags'
  | 'authors'
  | 'source'
  | 'events'
  | 'status'
  | 'slug'
  | 'brandKey'
  | 'qualifiers'
  | 'guides'
  | 'channels'
  | 'lead'
  | 'title'
  | 'body'
  | 'id'
  | 'format'
  | 'machineTags'
  | 'unpublishAt'
>;

export const DrawerContent: FC<DrawerContentProps> = memo(
  ({
    publishedAt,
    unpublishAt,
    source,
    status,
    renderInput,
    brandKey,
    qualifiers,
    channels,
    dispatch,
    title,
    format,
    body,
    machineTags,
    id,
  }) => {
    const classes = useStyles();

    const channelsCallback = useCallback(async () => {
      const { data: getBrandsData } = await client.query<
        GetBrandCategoriesQuery,
        GetBrandCategoriesQueryVariables
      >({
        query: GET_BRAND_CATEGORIES,
        variables: {
          brandKey: brandKey || BrandKey.CAP,
          format: CategoryFormat.Channel,
          status: CategoryStatus.Published,
        },
      });
      return getBrandsData.categories;
    }, [brandKey]);

    const scheduleDateComponent = renderInput('publishedAt') && (
      <TextField
        disabled={status === ArticleStatus.Published}
        id="datetime-local"
        label="Date de publication :"
        inputProps={{
          min: '1970-01-01T00:00',
          max: '2100-01-01T00:00',
        }}
        InputLabelProps={{
          shrink: true,
        }}
        onChange={(e: ChangeEvent<HTMLInputElement>) => {
          dispatch(
            actions.updateField({
              field: 'publishedAt',
              value: e.currentTarget.value,
            }),
          );
        }}
        type="datetime-local"
        value={
          publishedAt
            ? formatDate(new Date(publishedAt), "yyyy-MM-dd'T'HH:mm")
            : ''
        }
        variant="standard"
      />
    );

    const unpublishDateComponent = renderInput('unpublishAt') && (
      <TextField
        id="datetime-local"
        label="Date de dépublication :"
        inputProps={{
          min: formatDate(new Date(), "yyyy-MM-dd'T'HH:mm") || '',
          max: '2100-01-01T00:00',
        }}
        InputLabelProps={{
          shrink: true,
        }}
        onChange={(e: ChangeEvent<HTMLInputElement>) => {
          dispatch(
            actions.updateField({
              field: 'unpublishAt',
              value: e.currentTarget.value,
            }),
          );
        }}
        type="datetime-local"
        value={
          unpublishAt
            ? formatDate(new Date(unpublishAt), "yyyy-MM-dd'T'HH:mm")
            : ''
        }
        variant="standard"
      />
    );

    if (format === ArticleFormat.Rich) {
      return (
        <>
          <div className={classes.scheduleDate}>
            <div className="schedule-date-header">
              <DateRangeIcon />
              <Typography>Date de Programmation</Typography>
            </div>
            <div className="schedule-date-content">{scheduleDateComponent}</div>
          </div>
          <div className={classes.scheduleDate}>
            <div className="schedule-date-header">
              <DateRangeIcon />
              <Typography>Date de Dépublication</Typography>
            </div>
            <div className="schedule-date-content">
              {unpublishDateComponent}
            </div>
          </div>
          <LinksContent
            body={body}
            title={title}
            brandKey={brandKey}
            dispatch={dispatch}
            idArticle={id}
          />
          <LinksToTopic articleMachineTags={machineTags} />
        </>
      );
    }

    return (
      <div className={classes.grid}>
        <Typography variant="h6">{getTitleFromBrandKey(brandKey)}</Typography>
        {scheduleDateComponent}
        {renderInput('channel') && (
          <FormControl className={classes.formSuggest} variant="standard">
            <InputLabel shrink>Chaîne vidéo :</InputLabel>
            <Suggest
              selectedItems={channels.map(({ category }) => category)}
              placeholder="Lier à une chaine vidéos"
              suggestions={channelsCallback}
              multiple={false}
              isPrefetched
              onChange={(items) => {
                dispatch(
                  actions.updateField({
                    field: 'channels',
                    value: items.map((item) => ({
                      category: { id: item.id, title: item.title },
                    })),
                  }),
                );
              }}
            />
          </FormControl>
        )}

        {renderInput('source') && (
          <FormControl className={classes.formSuggest} variant="standard">
            <SourceInput
              brandKey={brandKey}
              source={source}
              dispatch={dispatch}
            />
          </FormControl>
        )}

        <Qualifiers
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          brandKey={brandKey!}
          qualifiers={qualifiers}
          articleFormat={format}
          onChange={(updatedQualifiers) => {
            dispatch(
              actions.updateField({
                field: 'qualifiers',
                value: updatedQualifiers,
              }),
            );
          }}
        />
      </div>
    );
  },
);
